/**
 * Turns a global search query into the terms a document viewer should highlight.
 *
 * The global search field accepts the advanced syntax documented in
 * `search-syntax-helper.tsx`, which the API hands to PostgreSQL's
 * `websearch_to_tsquery`:
 *
 * - `kontrakt brud`            — both words (AND)
 * - `"brud på kontrakt"`       — that exact phrase
 * - `kontrakt -ansættelse`     — excludes a word
 * - `kontrakt -"brud på ..."`  — excludes a phrase
 *
 * Search *tags* use the same syntax and are appended to the term before the
 * query is sent (see `GlobalSearchProvider.buildSearchTerm`), so a tag search
 * parses identically — the `÷` on a tag chip is only the icon for a `-` prefix.
 *
 * Highlighting the raw query string instead would mark the quote characters
 * literally, and — worse — would mark the very terms the reader excluded.
 */
export type SearchHighlightTerms = {
  /** Quoted phrases, to be matched whole. */
  phrases: string[];
  /** Bare words, each matched on its own. */
  words: string[];
};

/** Shorter than this, a term lights so much of a document that it is noise. */
export const MIN_TERM_LENGTH = 2;

type Token = { text: string; quoted: boolean; negated: boolean };

/** Splits on whitespace while keeping `"…"` groups together, tracking `-` prefixes. */
function tokenize(query: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;

  while (index < query.length) {
    while (index < query.length && /\s/.test(query[index])) index += 1;
    if (index >= query.length) break;

    let negated = false;
    if (query[index] === "-") {
      negated = true;
      index += 1;
    }

    if (query[index] === '"') {
      index += 1;
      const start = index;
      while (index < query.length && query[index] !== '"') index += 1;
      // An unmatched closing quote runs to the end, which websearch tolerates.
      tokens.push({ text: query.slice(start, index), quoted: true, negated });
      if (index < query.length) index += 1;
      continue;
    }

    const start = index;
    while (index < query.length && !/\s/.test(query[index])) index += 1;
    tokens.push({ text: query.slice(start, index), quoted: false, negated });
  }

  return tokens;
}

/**
 * Extracts the terms worth highlighting from a query. Excluded terms are
 * dropped rather than returned, so a caller cannot highlight them by mistake.
 */
export function parseSearchHighlightTerms(
  query: string | undefined
): SearchHighlightTerms {
  const phrases: string[] = [];
  const words: string[] = [];

  if (!query?.trim()) {
    return { phrases, words };
  }

  const seen = new Set<string>();

  for (const token of tokenize(query)) {
    if (token.negated) continue;

    const text = token.text.trim();
    if (text.length < MIN_TERM_LENGTH) continue;

    const key = `${token.quoted ? "p" : "w"}:${text.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);

    if (token.quoted) {
      phrases.push(text);
    } else {
      words.push(text);
    }
  }

  return { phrases, words };
}

/**
 * Flat list of terms to highlight, in order. Phrases come first so a phrase wins
 * over its own component words when both are present in the query.
 */
export function searchHighlightTermList(
  query: string | undefined
): string[] {
  const { phrases, words } = parseSearchHighlightTerms(query);
  return [...phrases, ...words];
}
